// ink-component
import React, { ReactNode } from 'react';
import { ThemeProvider } from 'styled-components';

import getGlobalProps from '../../helpers/constants/global-props';
import { VignetteTypes } from '../../helpers/constants/vignettes';
import Heading from '../Typography/Heading/Heading';
import Text from '../Typography/Text/Text';
import Vignette from '../Vignette/Vignette';

import { IconTextWrapper, IconWrapper } from './styles';

export interface IconWithTextProps {
  /** SVG for custom icon */
  svg: ReactNode;
  /** Text to accompany icon */
  text: ReactNode;
  /** Which size of placement */
  size: 'block' | 'page' | 'task' | 'mini';
  /** Which type of IconType */
  icon?: VignetteTypes;
  /** Marks the element with a given ID to make it easier to select in tests */
  'data-testid'?: string | number;
}

const globalProps = getGlobalProps({});

/**
 * @purpose Large text paired with either an icon or svg image
 * @categories illustration
 */

function defineSize(size: string) {
  switch (size) {
    case 'page':
      return 'lg';
    case 'mini':
      return 'sm';
    default:
      return 'md';
  }
}

function IconText(props: { size: string; text: ReactNode }) {
  switch (props.size) {
    case 'task':
      return <Heading variant="heading-4">{props.text}</Heading>;
    case 'mini':
      return <Text variant="body-3">{props.text}</Text>;
    default:
      return <Heading variant="heading-3">{props.text}</Heading>;
  }
}

function defineIcon(icon, size, svg) {
  if (icon) {
    return <Vignette aria-hidden="true" type={icon} size={defineSize(size)} />;
  }
  if (svg) {
    return svg;
  }
  return <Vignette aria-hidden="true" type="accept" />;
}

function IconWithText(props: IconWithTextProps) {
  const { size, icon, svg, text } = props;

  return (
    <ThemeProvider
      theme={currentTheme => ({
        ...currentTheme,
        size,
        tasks: icon === null || size === 'task',
      })}
    >
      <IconTextWrapper {...globalProps} data-testid={props['data-testid']}>
        <IconWrapper>{defineIcon(icon, size, svg)}</IconWrapper>
        <IconText size={size} text={text} />
      </IconTextWrapper>
    </ThemeProvider>
  );
}

IconWithText.defaultProps = {
  size: 'block',
  icon: null,
  svg: null,
  'data-testid': undefined,
};

IconWithText.displayName = 'IconWithText';

export default IconWithText;
